<template>
  <section class="products-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">产品服务</h2>
        <p class="section-subtitle">专业的技术解决方案，助力企业数字化转型</p>
      </div>

      <div class="products-grid">
        <div
          v-for="product in displayProducts"
          :key="product.id"
          class="product-card"
          @click="goToProductDetail(product.id)"
        >
          <div class="product-image">
            <img
              :src="product.imageUrl || '/images/product-default.jpg'"
              :alt="product.name"
              @error="handleImageError"
            >
            <div class="product-overlay">
              <el-button type="primary" size="small">查看详情</el-button>
            </div>
          </div>

          <div class="product-content">
            <h3 class="product-name">{{ product.name }}</h3>
            <p class="product-description">{{ product.description }}</p>

            <div class="product-features">
              <span
                v-for="feature in getProductFeatures(product)"
                :key="feature"
                class="feature-tag"
              >
                {{ feature }}
              </span>
            </div>

            <div class="product-footer">
              <div class="product-category">{{ product.category }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-footer">
        <el-button type="primary" size="large" @click="goToProducts">
          查看更多产品
        </el-button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import { preloadImage, handleImageError } from '@/utils/imageUtils'

export default {
  name: 'ProductsSection',
  computed: {
    ...mapState(['products']),
    displayProducts () {
      return this.products.slice(0, 6) // 首页只显示前6个产品
    }
  },
  methods: {
    async getProductImageUrl (product) {
      if (!product.imageUrl) {
        return '/images/product-default.jpg'
      }
      return await preloadImage(product.imageUrl, 'product')
    },
    getProductFeatures (product) {
      if (!product.features) return []
      return product.features.split(',').slice(0, 3) // 最多显示3个特性
    },
    goToProductDetail (id) {
      this.$router.push(`/products/${id}`)
    },
    goToProducts () {
      this.$router.push('/products')
    },
    handleImageError (event) {
      return handleImageError(event, 'product')
    }
  }
}
</script>

<style lang="scss" scoped>
.products-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: $text-primary;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: $text-secondary;
  max-width: 600px;
  margin: 0 auto;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
}

.product-card {
  background: white;
  border-radius: $border-radius-large;
  box-shadow: $box-shadow-base;
  overflow: hidden;
  transition: $transition-base;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: $box-shadow-light;

    .product-overlay {
      opacity: 1;
    }
  }
}

.product-image {
  position: relative;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: $transition-base;
  }

  .product-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: $transition-base;
  }
}

.product-content {
  padding: 24px;
}

.product-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 12px;
  line-height: 1.4;
}

.product-description {
  font-size: 0.9rem;
  color: $text-secondary;
  line-height: 1.6;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.feature-tag {
  background: linear-gradient(135deg, $primary-color, lighten($primary-color, 20%));
  color: white;
  font-size: 0.8rem;
  padding: 4px 8px;
  border-radius: $border-radius-small;
  font-weight: 500;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-price {
  .price {
    font-size: 1.2rem;
    font-weight: 700;
    color: $primary-color;
  }

  .currency {
    font-size: 0.8rem;
    color: $text-secondary;
    margin-left: 4px;
  }
}

.product-category {
  font-size: 0.8rem;
  color: $text-secondary;
  background: $border-lighter;
  padding: 4px 8px;
  border-radius: $border-radius-small;
}

.section-footer {
  text-align: center;
}

// 响应式设计
@media (max-width: 768px) {
  .products-section {
    padding: 60px 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .product-card {
    &:hover {
      transform: none;
    }
  }
}
</style>
